import React, { type FC, useCallback, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { Search as SearchIcon } from '@mui/icons-material';
import {
    Box,
    TextField,
    InputAdornment,
    Typography,
    Autocomplete,
    type AutocompleteProps,
    MenuItem
} from '@mui/material';
import Fuse from 'fuse.js';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import ConfigSection, { ComponentType, type FieldType } from './ConfigSection';
import {
    ChatPermission,
    ClassAccess,
    ClassLevelOverrideOptions,
    InstructorTARestrictedAccess,
    OpenIdPromptType,
    ScreenShareResolutions
} from '../../../../types/adminConfig';
import { Permission } from '../../../../types/recordingTypes';
import AdminAuthContainer from '../../AdminAuthContainer';
import AdminSettingsContainer from '../../AdminSettingsContainer';
import TopLevelTabs, { ConfigTabs } from '../../TopLevelTabs';
import { showNonCollabSetting } from '../../utils/showAdvancedSetting';
import VirtualBackgrounds from '../../VirtualBackgrounds';
import SaveButton from '../SaveButton';

const SettingsContainer = styled(Box)`
    padding: 16px;
`;

const HeadingText = styled(Typography)({
    fontWeight: 500,
    fontSize: '26px',
    whiteSpace: 'nowrap',
    marginBottom: '24px'
});

const HeaderRow = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const StyledMenuItem = styled(MenuItem)`
    white-space: normal;
    word-break: break-word;
`;

type FlatSearchItem = {
    key: string;
    label: string;
    tab: ConfigTabs;
    sectionTitle: string;
    fieldRef: FieldType;
};

const StyledAutocomplete = styled(
    Autocomplete as React.ComponentType<AutocompleteProps<FlatSearchItem, boolean, boolean, boolean>>
)`
    min-width: 340px;
`;

const SaveButtonContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '16px 0 0 0'
});

const ConfigurationPage: FC = () => {
    const { t } = useTranslation('admin_settings');
    const [activeTab, setActiveTab] = useState<ConfigTabs>(ConfigTabs.BeforeSession);
    const {
        isSavingSettingsData,
        isSavingDisabled,
        adminSettings,
        school,
        loadingSchool,
        setAdminSettings,
        handleSaveSettings
    } = useContainer(AdminSettingsContainer);
    const { isAdmin, isSuperAdmin } = useContainer(AdminAuthContainer);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const nonCollabSettingShown = useMemo(
        () => showNonCollabSetting({ school, loadingSchool, isSuperAdmin }),
        [school, loadingSchool, isSuperAdmin]
    );

    const isTeams = school?.isTeams;
    const downloadsDisabled = Boolean(adminSettings.featureSettings.options.recordingsDisableDownloads);
    const initialPermissionPublic = adminSettings.featureSettings.recordingInitialPermission === 'PUBLIC';

    const [values, setValues] = useState({
        earlyJoinMinutes: adminSettings.featureSettings.options.allowStudentJoinPriorMinutes > 0,
        attendanceTabAccessSwitch: adminSettings.featureSettings.attendanceTabAccess !== ClassAccess.None,
        attendanceGradeTardyScoreSwitch: adminSettings.featureSettings.options.attendanceGradeTardyScore > 0,
        hasEndUserSurveySwitch:
            adminSettings.featureSettings.options.hasEndUserSurvey !== InstructorTARestrictedAccess.NoOne,
        classAccessSwitch: adminSettings.featureSettings.classAccess !== ClassAccess.None,
        recordingTabAccessSwitch: adminSettings.featureSettings.recordingTabAccess !== ClassAccess.None
    });

    const onSwitchChange = useCallback(
        (key: string, value: any) => {
            setAdminSettings((prev) => {
                const updatedFeatureSettings = { ...prev.featureSettings };
                const updatedOptions = { ...updatedFeatureSettings.options };

                // Handle value resets
                if (!value) {
                    switch (key) {
                        case 'earlyJoinMinutes':
                            updatedOptions.allowStudentJoinPriorMinutes = 0;
                            break;
                        case 'attendanceGradeTardyScoreSwitch':
                            updatedOptions.attendanceGradeTardyScore = 0;
                            break;
                        case 'attendanceAutoLateThresholdSwitch':
                            updatedOptions.attendanceAutoLateThresholdMinutes = 0;
                            break;
                        case 'hasEndUserSurveySwitch':
                            updatedOptions.hasEndUserSurvey = InstructorTARestrictedAccess.NoOne;
                            break;
                        case 'attendanceTabAccessSwitch':
                            updatedFeatureSettings.attendanceTabAccess = ClassAccess.None;
                            break;
                        case 'classAccessSwitch':
                            updatedFeatureSettings.classAccess = ClassAccess.None;
                            break;
                        case 'recordingTabAccessSwitch':
                            updatedFeatureSettings.recordingTabAccess = ClassAccess.None;
                            break;
                    }
                }

                return {
                    ...prev,
                    featureSettings: {
                        ...updatedFeatureSettings,
                        options: updatedOptions
                    }
                };
            });

            setValues((prev) => ({ ...prev, [key]: value }));
        },
        [setAdminSettings, setValues]
    );

    const onOpenIdPromptChange = useCallback(
        (key: string, value: OpenIdPromptType) => {
            const newValue = value === OpenIdPromptType.NotSpecified ? [] : [value];

            setAdminSettings((prev) => ({
                ...prev,
                featureSettings: {
                    ...prev.featureSettings,
                    options: {
                        ...prev.featureSettings.options,
                        [key]: newValue
                    }
                }
            }));
        },
        [setAdminSettings]
    );

    const onFeatureOptionChange = useCallback(
        (key: string, value: any) => {
            setAdminSettings((prev) => {
                const updatedOptions = {
                    ...prev.featureSettings.options,
                    [key]: value
                };

                if (key === 'hasNextGenOneDrive' && !value) {
                    // Reset OpenID prompt when OneDrive is toggled off
                    updatedOptions.openIdPrompt = [OpenIdPromptType.Consent];
                }

                return {
                    ...prev,
                    featureSettings: {
                        ...prev.featureSettings,
                        options: updatedOptions
                    }
                };
            });
        },
        [setAdminSettings]
    );

    const onUpdateOverrides = useCallback(
        (key: string, value: any) => {
            setAdminSettings((prev) => ({
                ...prev,
                featureSettings: {
                    ...prev.featureSettings,
                    overrides: {
                        ...prev.featureSettings.overrides,
                        [key]: value
                    }
                }
            }));
        },
        [setAdminSettings]
    );

    const onFeatureSettingsChange = useCallback(
        (key: string, value: any) => {
            setAdminSettings((prev) => ({
                ...prev,
                featureSettings: {
                    ...prev.featureSettings,
                    [key]: value
                }
            }));
        },
        [setAdminSettings]
    );

    const onAuthSettingsChange = useCallback(
        (key: string, value: any) => {
            setAdminSettings((prev) => ({
                ...prev,
                authSettings: {
                    ...prev.authSettings,
                    [key]: value
                }
            }));
        },
        [setAdminSettings]
    );

    const onRecordingSettingsChange = useCallback(
        (key: string, value: any) => {
            setAdminSettings((prev) => {
                let updates = {};

                if (key === 'autoSave') {
                    updates = {
                        autoSave: value,
                        cloudSave: value,
                        localSave: false
                    };
                } else if (key === 'participantsCanDownload') {
                    updates = { participantsCanDownload: value };
                }

                return {
                    ...prev,
                    recordingSettings: {
                        ...prev.recordingSettings,
                        ...updates
                    }
                };
            });
        },
        [setAdminSettings]
    );

    const onEmbedDomainsChange = useCallback(
        (key: string, value: string) => {
            const cleanedDomains = value
                .split(',')
                .map((d) => d.trim().toLowerCase())
                .filter(Boolean); // removes empty strings

            setAdminSettings((prev) => ({
                ...prev,
                featureSettings: {
                    ...prev.featureSettings,
                    options: {
                        ...prev.featureSettings.options,
                        [key]: cleanedDomains
                    }
                }
            }));
        },
        [setAdminSettings]
    );

    const dialogOptions = useMemo(
        () => ({
            title: '',
            description: t('whoCanOverride'),
            statePath: '',
            onChange: onUpdateOverrides,
            key: '',
            options: [
                { key: ClassLevelOverrideOptions.NoOne, label: t('configuration.override.no-one') },
                { key: ClassLevelOverrideOptions.AdminsOnly, label: t('configuration.override.administrators') },
                {
                    key: ClassLevelOverrideOptions.AdminsAndInstructors,
                    label: t('configuration.override.admins-and-instructors')
                }
            ]
        }),
        [t, onUpdateOverrides]
    );

    const handleClassInvitationEmailAddress = useCallback(
        (key: string, value: string) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const isEmailValid = !value || emailRegex.test(value);

            // Update error state
            setFieldErrors((prev) => {
                if (!isEmailValid) {
                    return {
                        ...prev,
                        [key]: t('users.dialogs.error-valid-email-required')
                    };
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { [key]: _, ...rest } = prev;
                    return rest;
                }
            });

            setAdminSettings((prev) => ({
                ...prev,
                featureSettings: {
                    ...prev.featureSettings,
                    options: {
                        ...prev.featureSettings.options,
                        [key]: value
                    }
                }
            }));
        },
        [setAdminSettings, t]
    );

    const beforeSessionConfigSections = useMemo(
        () => [
            {
                title: t('admin_settings:configuration.tabs.before-session.communications'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.inviteEmails'),
                        key: 'sendInviteEmails',
                        statePath: 'featureSettings.sendInviteEmails',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin,
                        children: [
                            {
                                type: ComponentType.TextField,
                                label: t('configuration.class-invitation-email-address-override'),
                                key: 'classInvitationEmailAddressOverride',
                                tooltip: t('configuration.class-invitation-email-address-override-helper-text'),
                                statePath: 'featureSettings.options.classInvitationEmailAddressOverride',
                                onChange: handleClassInvitationEmailAddress
                            }
                        ]
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.before-session.joining-session'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.allowWaitingRoom'),
                        tooltip: t('configuration.allowWaitingRoomHint'),
                        key: 'allowWaitingRoom',
                        statePath: 'featureSettings.options.allowWaitingRoom',
                        onChange: onFeatureOptionChange,
                        hidden: !isTeams
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.join-options.title'),
                        key: 'earlyJoinMinutes',
                        onChange: onSwitchChange,
                        overrideDialogOption: {
                            ...dialogOptions,
                            key: 'allowStudentJoinPriorMinutes',
                            statePath: 'featureSettings.overrides.allowStudentJoinPriorMinutes',
                            title: t('configuration.join-options.title')
                        },
                        tooltip: t('configuration.join-options.student'),
                        children: [
                            {
                                type: ComponentType.Slider,
                                label: '',
                                key: 'allowStudentJoinPriorMinutes',
                                onChange: onFeatureOptionChange,
                                statePath: 'featureSettings.options.allowStudentJoinPriorMinutes',
                                sliderOptions: {
                                    min: 0,
                                    max: 60,
                                    marks: [
                                        { value: 0 },
                                        { value: 5, label: '5' },
                                        { value: 10 },
                                        { value: 15, label: '15' },
                                        { value: 20 },
                                        { value: 25 },
                                        { value: 30, label: '30' },
                                        { value: 35 },
                                        { value: 40 },
                                        { value: 45, label: '45' },
                                        { value: 50 },
                                        { value: 55 },
                                        { value: 60, label: '60' }
                                    ],
                                    valueLabelFormat: (value: number) => `${value} ${t('configuration.minutes')}`
                                }
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.autoJoinWhenSessionActive'),
                        key: 'autoJoinWhenSessionActive',
                        statePath: 'featureSettings.options.autoJoinWhenSessionActive',
                        onChange: onFeatureOptionChange,
                        children: [
                            {
                                type: ComponentType.Slider,
                                label: t('configuration.join-options.instructor'),
                                key: 'allowInstructorAutoJoinPriorMinutes',
                                onChange: onFeatureOptionChange,
                                statePath: 'featureSettings.options.allowInstructorAutoJoinPriorMinutes',
                                sliderOptions: {
                                    min: 0,
                                    max: 60,
                                    marks: [
                                        { value: 0 },
                                        { value: 5, label: '5' },
                                        { value: 10 },
                                        { value: 15, label: '15' },
                                        { value: 20 },
                                        { value: 25 },
                                        { value: 30, label: '30' },
                                        { value: 35 },
                                        { value: 40 },
                                        { value: 45, label: '45' },
                                        { value: 50 },
                                        { value: 55 },
                                        { value: 60, label: '60' }
                                    ],
                                    valueLabelFormat: (value: number) => `${value} ${t('configuration.minutes')}`
                                }
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('user-access.class-access.header'),
                        key: 'classAccessSwitch',
                        onChange: onSwitchChange,
                        children: [
                            {
                                type: ComponentType.Select,
                                label: '',
                                key: 'classAccess',
                                statePath: 'featureSettings.classAccess',
                                onChange: onFeatureSettingsChange,
                                options: [
                                    { label: t('user-access.class-access.options.all'), value: ClassAccess.All },
                                    { label: t('user-access.class-access.options.app'), value: ClassAccess.App },
                                    { label: t('user-access.class-access.options.lti'), value: ClassAccess.Lti },
                                    { label: t('user-access.class-access.options.none'), value: ClassAccess.None }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.before-session.attendance'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('user-access.attendance-participants-allow'),
                        key: 'attendanceTabAccessSwitch',
                        onChange: onSwitchChange,
                        children: [
                            {
                                type: ComponentType.Select,
                                label: '',
                                key: 'attendanceTabAccess',
                                statePath: 'featureSettings.attendanceTabAccess',
                                onChange: onFeatureSettingsChange,
                                options: [
                                    { label: t('user-access.class-access.options.all'), value: ClassAccess.All },
                                    { label: t('user-access.class-access.options.app'), value: ClassAccess.App },
                                    { label: t('user-access.class-access.options.lti'), value: ClassAccess.Lti },
                                    { label: t('user-access.class-access.options.none'), value: ClassAccess.None }
                                ]
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.attendance-config.show-computed-attendance-grade-on-attendance-tab'),
                        key: 'showAttendanceGrade',
                        statePath: 'featureSettings.options.showAttendanceGrade',
                        onChange: onFeatureOptionChange,
                        overrideDialogOption: {
                            ...dialogOptions,
                            key: 'showAttendanceGrade',
                            statePath: 'featureSettings.overrides.showAttendanceGrade',
                            title: t('configuration.attendance-config.show-computed-attendance-grade-on-attendance-tab')
                        }
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('lms-attendance-sync-tardy-score'),
                        key: 'attendanceGradeTardyScoreSwitch',
                        overrideDialogOption: {
                            ...dialogOptions,
                            key: 'attendanceGradeTardyScore',
                            statePath: 'featureSettings.overrides.attendanceGradeTardyScore',
                            title: t('lms-attendance-sync-tardy-score')
                        },
                        tooltip: t('lms-attendance-sync-tardy-score-helper'),
                        onChange: onSwitchChange,
                        children: [
                            {
                                type: ComponentType.Slider,
                                label: '',
                                key: 'attendanceGradeTardyScore',
                                onChange: onFeatureOptionChange,
                                statePath: 'featureSettings.options.attendanceGradeTardyScore',
                                sliderOptions: {
                                    min: 0,
                                    max: 100,
                                    marks: [
                                        { value: 0, label: '0%' },
                                        { value: 10 },
                                        { value: 20, label: '20%' },
                                        { value: 30 },
                                        { value: 40, label: '40%' },
                                        { value: 50 },
                                        { value: 60, label: '60%' },
                                        { value: 70 },
                                        { value: 80, label: '80%' },
                                        { value: 90 },
                                        { value: 100, label: '100%' }
                                    ],
                                    valueLabelFormat: (value: number) => `${value}%`
                                }
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.attendance-config.auto-attendance-settings'),
                        key: 'attendanceAutoLateThresholdSwitch',
                        overrideDialogOption: {
                            ...dialogOptions,
                            key: 'attendanceAutoLateThresholdMinutes',
                            statePath: 'featureSettings.overrides.attendanceAutoLateThresholdMinutes',
                            title: t('configuration.attendance-config.auto-attendance-settings')
                        },
                        tooltip: t('configuration.attendance-config.late-threshold-hint'),
                        onChange: onSwitchChange,
                        children: [
                            {
                                type: ComponentType.Slider,
                                label: '',
                                key: 'attendanceAutoLateThresholdMinutes',
                                onChange: onFeatureOptionChange,
                                statePath: 'featureSettings.options.attendanceAutoLateThresholdMinutes',
                                sliderOptions: {
                                    min: 5,
                                    max: 60,
                                    marks: [
                                        { value: 5, label: '5' },
                                        { value: 10 },
                                        { value: 15, label: '15' },
                                        { value: 20 },
                                        { value: 25 },
                                        { value: 30, label: '30' },
                                        { value: 35 },
                                        { value: 40 },
                                        { value: 45, label: '45' },
                                        { value: 50 },
                                        { value: 55 },
                                        { value: 60, label: '60' }
                                    ],
                                    valueLabelFormat: (value: number) => `${value} ${t('configuration.minutes')}`
                                }
                            }
                        ]
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.before-session.other-settings'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.allowInstructorSwap'),
                        key: 'allowInstructorSwap',
                        statePath: 'featureSettings.allowInstructorSwap',
                        onChange: onFeatureSettingsChange,
                        hidden: isTeams
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('user-access.restrict-sessions-recordings-by-groups'),
                        tooltip: t('user-access.restrict-sessions-recordings-by-groups-hint'),
                        key: 'restrictSessionsRecordingsByGroups',
                        statePath: 'featureSettings.restrictSessionsRecordingsByGroups',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    }
                ]
            }
        ],
        [
            t,
            isAdmin,
            isTeams,
            handleClassInvitationEmailAddress,
            dialogOptions,
            onSwitchChange,
            onFeatureOptionChange,
            onFeatureSettingsChange
        ]
    );

    const inSessionConfigSections = useMemo(
        () => [
            {
                title: t('admin_settings:configuration.tabs.in-session.content'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.share-files'),
                        key: 'hasShareFiles',
                        disabled: !isAdmin,
                        statePath: 'featureSettings.hasShareFiles',
                        onChange: onFeatureSettingsChange,
                        children: [
                            {
                                type: ComponentType.Switch,
                                label: t('configuration.hasNextGenGoogleDrive'),
                                key: 'hasNextGenGoogleDrive',
                                statePath: 'featureSettings.options.hasNextGenGoogleDrive',
                                onChange: onFeatureOptionChange
                            },
                            {
                                type: ComponentType.Switch,
                                label: t('configuration.hasNextGenOneDrive'),
                                key: 'hasNextGenOneDrive',
                                statePath: 'featureSettings.options.hasNextGenOneDrive',
                                onChange: onFeatureOptionChange,
                                children: [
                                    {
                                        type: ComponentType.Select,
                                        label: t('configuration.openid-prompt'),
                                        key: 'openIdPrompt',
                                        statePath: 'featureSettings.options.openIdPrompt',
                                        onChange: onOpenIdPromptChange,
                                        options: [
                                            {
                                                label: t('configuration.openid-prompt-types.not-specified'),
                                                value: OpenIdPromptType.NotSpecified
                                            },
                                            {
                                                label: t('configuration.openid-prompt-types.none'),
                                                value: OpenIdPromptType.None
                                            },
                                            {
                                                label: t('configuration.openid-prompt-types.consent'),
                                                value: OpenIdPromptType.Consent
                                            },
                                            {
                                                label: t('configuration.openid-prompt-types.login'),
                                                value: OpenIdPromptType.Login
                                            },
                                            {
                                                label: t('configuration.openid-prompt-types.select-account'),
                                                value: OpenIdPromptType.SelectAccount
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.syllabus'),
                        key: 'classDocument',
                        statePath: 'featureSettings.classDocument',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.activities'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.allow-presentations'),
                        key: 'hasPresentations',
                        statePath: 'featureSettings.options.hasPresentations',
                        onChange: onFeatureOptionChange,
                        overrideDialogOption: {
                            ...dialogOptions,
                            key: 'hasPresentations',
                            statePath: 'featureSettings.overrides.hasPresentations',
                            title: t('configuration.allow-presentations')
                        }
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.next-gen-polls'),
                        key: 'hasNextGenPolls',
                        statePath: 'featureSettings.options.hasNextGenPolls',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.whiteboard'),
                        key: 'whiteboard',
                        statePath: 'featureSettings.whiteboard',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.enable-learning-tools-transition'),
                        key: 'enableLearningToolsTransition',
                        statePath: 'featureSettings.options.enableLearningToolsTransition',
                        onChange: onFeatureOptionChange,
                        tooltip: t('configuration.enable-learning-tools-transition-hint'),
                        disabled: !isAdmin
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.lms-tools'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.assessments'),
                        key: 'classAssessments',
                        statePath: 'featureSettings.classAssessments',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.assignments'),
                        key: 'assignments',
                        statePath: 'featureSettings.assignments',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.polling'),
                        key: 'polling',
                        statePath: 'featureSettings.polling',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.surveys'),
                        key: 'surveys',
                        statePath: 'featureSettings.surveys',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.lms'),
                        key: 'lms',
                        statePath: 'featureSettings.lms',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.gradebook'),
                        key: 'classGradebook',
                        statePath: 'featureSettings.classGradebook',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.breakout-rooms'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.hasAllBreakoutTranscripts'),
                        key: 'hasAllBreakoutTranscripts',
                        statePath: 'featureSettings.options.hasAllBreakoutTranscripts',
                        onChange: onFeatureOptionChange,
                        hidden: (() => {
                            if (isTeams) {
                                const { hasTeamsLiveCaptioning, offerBreakoutTranscripts } =
                                    adminSettings.featureSettings.options;

                                // in Teams, BOTH hasTeamsLiveCaptioning and offerBreakoutTranscripts must be true!
                                return !hasTeamsLiveCaptioning || !offerBreakoutTranscripts;
                            }

                            return !adminSettings.featureSettings.options.offerBreakoutTranscripts;
                        })(),
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.enable-whiteboard-bor-type'),
                        key: 'hasWhiteboardBORType',
                        statePath: 'featureSettings.options.hasWhiteboardBORType',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.chat'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.enable-chat'),
                        key: 'chat',
                        statePath: 'featureSettings.chat',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin,
                        children: [
                            {
                                type: ComponentType.Select,
                                label: t('configuration.default-chat-permissions'),
                                key: 'defaultChatPermissions',
                                statePath: 'featureSettings.options.defaultChatPermissions',
                                onChange: onFeatureOptionChange,
                                options: [
                                    {
                                        label: t('configuration.chat-permissions-options.no-one'),
                                        value: ChatPermission.NoOne
                                    },
                                    {
                                        label: t('configuration.chat-permissions-options.instructor-only'),
                                        value: ChatPermission.InstructorOnly
                                    },
                                    {
                                        label: t('configuration.chat-permissions-options.everyone-publicly'),
                                        value: ChatPermission.EveryonePublicly
                                    },
                                    {
                                        label: t('configuration.chat-permissions-options.everyone-directly'),
                                        value: ChatPermission.EveryoneDirectly
                                    }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.other-tools'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.loseFocusTracking'),
                        key: 'loseFocusTracking',
                        statePath: 'featureSettings.loseFocusTracking',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('auth.allowPMI'),
                        key: 'allowPMI',
                        statePath: 'authSettings.allowPMI',
                        onChange: onAuthSettingsChange,
                        disabled: !isAdmin,
                        hidden: !nonCollabSettingShown || isTeams
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.enable-learner-engagement'),
                        key: 'enableLearnerEngagement',
                        statePath: 'featureSettings.options.enableLearnerEngagement',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.proctoring'),
                        key: 'proctoring',
                        statePath: 'featureSettings.proctoring',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.enable-in-meeting-tech-check'),
                        key: 'hasInMeetingTechCheck',
                        statePath: 'featureSettings.options.hasInMeetingTechCheck',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.other-settings'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.showMeetingInfo'),
                        key: 'showMeetingInfo',
                        statePath: 'featureSettings.showMeetingInfo',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin,
                        tooltip: isTeams ? t('configuration.showMeetingHint-teams') : t('configuration.showMeetingHint')
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.enable-next-gen-chat-activity-feed'),
                        key: 'hasNextGenChatActivityFeed',
                        statePath: 'featureSettings.options.hasNextGenChatActivityFeed',
                        tooltip: t('configuration.enable-next-gen-chat-activity-feed-hint'),
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin || !adminSettings.featureSettings.options.hasNextGenChat
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.permitted-embed-domains'),
                        key: 'permittedEmbedDomainsSwitch',
                        tooltip: t('configuration.permitted-embed-domains-helper-text'),
                        onChange: onSwitchChange,
                        disabled: !isAdmin,
                        children: [
                            {
                                type: ComponentType.TextField,
                                label: '',
                                key: 'permittedEmbedDomains',
                                statePath: 'featureSettings.options.permittedEmbedDomains',
                                onChange: onEmbedDomainsChange
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.excluded-embed-domains'),
                        key: 'excludedEmbedDomainsSwitch',
                        tooltip: t('configuration.excluded-embed-domains-helper-text'),
                        onChange: onSwitchChange,
                        disabled: !isAdmin,
                        children: [
                            {
                                type: ComponentType.TextField,
                                label: '',
                                key: 'excludedEmbedDomains',
                                statePath: 'featureSettings.options.excludedEmbedDomains',
                                onChange: onEmbedDomainsChange
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.orgScreenshareRes'),
                        key: 'orgScreenshareResSwitch',
                        onChange: onSwitchChange,
                        children: [
                            {
                                type: ComponentType.Select,
                                label: '',
                                key: 'orgScreenshareRes',
                                statePath: 'featureSettings.options.orgScreenshareRes',
                                onChange: onFeatureOptionChange,
                                options: [
                                    {
                                        label: t('screenshare.resolutions.low'),
                                        value: ScreenShareResolutions.Low,
                                        disabled: !isTeams
                                    },
                                    {
                                        label: t('screenshare.resolutions.medium'),
                                        value: ScreenShareResolutions.Medium,
                                        disabled: false
                                    },
                                    {
                                        label: t('screenshare.resolutions.high'),
                                        value: ScreenShareResolutions.High,
                                        disabled: false
                                    }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.recordings'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.disableCameraMicrophoneWhenRecording'),
                        key: 'disableCameraMicrophoneWhenRecording',
                        statePath: 'featureSettings.options.disableCameraMicrophoneWhenRecording',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.record-automatic'),
                        key: 'autoSave',
                        statePath: 'recordingSettings.autoSave',
                        onChange: onRecordingSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.enable-breakout-room-cloud-recording'),
                        key: 'enableBreakoutRoomCloudRecording',
                        statePath: 'featureSettings.enableBreakoutRoomCloudRecording',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.virtual-backgrounds'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: 'Upload virtual backgrounds for your organization',
                        key: 'virtualBackgroundsOrgUpload',
                        collapsible: true,
                        hideSwitch: true,
                        collapseContent: 'uploadVirtualBackgrounds',
                        tooltip: 'Tool tip for uploading virtual backgrounds. <LINK>Learn more</LINK>',
                        tooltipLink: 'https://support.class.com/s/article/Admin-Configuration?language=en_US'
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.allowCustomVirtualBackgroundUpload'),
                        key: 'allowCustomVirtualBackgroundUpload',
                        statePath: 'featureSettings.options.allowCustomVirtualBackgroundUpload',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.disableClassBackgrounds'),
                        key: 'disableClassBackgrounds',
                        statePath: 'featureSettings.options.disableClassBackgrounds',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin,
                        tooltip: t('configuration.disableClassBackgroundsHint')
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.allowVirtualBackgroundInModeratePerfMode'),
                        key: 'allowVirtualBackgroundInModeratePerfMode',
                        statePath: 'featureSettings.options.allowVirtualBackgroundInModeratePerfMode',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    }
                ],
                slots: {
                    uploadVirtualBackgrounds: <VirtualBackgrounds />
                }
            }
        ],
        [
            isAdmin,
            t,
            nonCollabSettingShown,
            isTeams,
            dialogOptions,
            adminSettings,
            onEmbedDomainsChange,
            onSwitchChange,
            onFeatureOptionChange,
            onFeatureSettingsChange,
            onAuthSettingsChange,
            onRecordingSettingsChange,
            onOpenIdPromptChange
        ]
    );

    const afterSessionConfigSections = useMemo(
        () => [
            {
                title: t('admin_settings:configuration.tabs.after-session.feedback-surveys'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.hasEndUserSurvey'),
                        key: 'hasEndUserSurveySwitch',
                        onChange: onSwitchChange,
                        children: [
                            {
                                type: ComponentType.Select,
                                label: '',
                                key: 'hasEndUserSurvey',
                                statePath: 'featureSettings.options.hasEndUserSurvey',
                                onChange: onFeatureOptionChange,
                                options: [
                                    {
                                        label: t('configuration.InstructorTARestrictedAccess.Everyone'),
                                        value: InstructorTARestrictedAccess.Everyone
                                    },
                                    {
                                        label: t('configuration.InstructorTARestrictedAccess.InstructorsTAsOnly'),
                                        value: InstructorTARestrictedAccess.InstructorsTAsOnly
                                    },
                                    {
                                        label: t('configuration.InstructorTARestrictedAccess.NoOne'),
                                        value: InstructorTARestrictedAccess.NoOne
                                    }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.in-session.recordings'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('user-access.recordings-tab-access'),
                        key: 'recordingTabAccessSwitch',
                        onChange: onSwitchChange,
                        children: [
                            {
                                type: ComponentType.Select,
                                label: '',
                                key: 'recordingTabAccess',
                                statePath: 'featureSettings.recordingTabAccess',
                                onChange: onFeatureSettingsChange,
                                options: [
                                    { label: t('user-access.class-access.options.all'), value: ClassAccess.All },
                                    { label: t('user-access.class-access.options.app'), value: ClassAccess.App },
                                    { label: t('user-access.class-access.options.lti'), value: ClassAccess.Lti },
                                    { label: t('user-access.class-access.options.none'), value: ClassAccess.None }
                                ]
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.recordings.permission'),
                        key: 'recordingInitialPermissionSwitch',
                        onChange: onSwitchChange,
                        overrideDialogOption: {
                            ...dialogOptions,
                            key: 'recordingInitialPermission',
                            statePath: 'featureSettings.overrides.recordingInitialPermission',
                            title: t('configuration.recordings.permission')
                        },
                        children: [
                            {
                                type: ComponentType.Select,
                                label: '',
                                key: 'recordingInitialPermission',
                                statePath: 'featureSettings.recordingInitialPermission',
                                onChange: onFeatureSettingsChange,
                                options: [
                                    {
                                        label: t('lti:recordings.permissions.admins'),
                                        value: Permission.ADMINS,
                                        disabled: false
                                    },
                                    {
                                        label: t('lti:recordings.permissions.roster'),
                                        value: Permission.ROSTER,
                                        disabled: false
                                    },
                                    {
                                        label: t('lti:recordings.permissions.public'),
                                        value: Permission.PUBLIC,
                                        disabled: adminSettings.featureSettings.options.recordingsDisablePublicOption
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('user-access.recording-participants-can-view'),
                        key: 'recordingParticipantsCanView',
                        statePath: 'featureSettings.recordingParticipantsCanView',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.recordings.disable-downloads'),
                        key: 'recordingsDisableDownloads',
                        statePath: 'featureSettings.options.recordingsDisableDownloads',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('user-access.recording-participants-can-download'),
                        key: 'participantsCanDownload',
                        statePath: 'recordingSettings.participantsCanDownload',
                        onChange: onRecordingSettingsChange,
                        tooltip: downloadsDisabled
                            ? t('configuration.recordings.downloads-disabled-cannot-allow-participants')
                            : '',
                        disabled: !isAdmin || downloadsDisabled
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.recordings.restrict-gallery'),
                        key: 'recordingRestrictGallery',
                        statePath: 'featureSettings.recordingRestrictGallery',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin,
                        hidden: isTeams
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.recordings.restrict-chat-participants'),
                        key: 'recordingsRestrictChatParticipants',
                        statePath: 'featureSettings.options.recordingsRestrictChatParticipants',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin,
                        overrideDialogOption: {
                            ...dialogOptions,
                            key: 'recordingsRestrictChatParticipants',
                            statePath: 'featureSettings.overrides.recordingsRestrictChatParticipants',
                            title: t('configuration.recordings.restrict-chat-participants')
                        }
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.recordings.disable-public-option'),
                        key: 'recordingsDisablePublicOption',
                        statePath: 'featureSettings.options.recordingsDisablePublicOption',
                        onChange: onFeatureOptionChange,
                        tooltip: initialPermissionPublic
                            ? t('configuration.recordings.disable-public-option-error')
                            : t('configuration.recordings.disable-public-option-hint'),
                        disabled: !isAdmin || initialPermissionPublic
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.recordings.exclude-students-active-speaker'),
                        key: 'recordingsExcludeStudentsActiveSpeaker',
                        statePath: 'featureSettings.options.recordingsExcludeStudentsActiveSpeaker',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin,
                        hidden: isTeams
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.recordings.enable-bulk-operations'),
                        key: 'bulkRecordingOperations',
                        statePath: 'featureSettings.options.bulkRecordingOperations',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    }
                ]
            },
            {
                title: t('admin_settings:configuration.tabs.after-session.other-settings'),
                collapsible: true,
                fields: [
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.instructor-chat-download'),
                        key: 'instructorChatDownload',
                        statePath: 'featureSettings.instructorChatDownload',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.allowInstructorsTranscriptDownload'),
                        key: 'allowInstructorsTranscriptDownload',
                        statePath: 'featureSettings.options.allowInstructorsTranscriptDownload',
                        onChange: onFeatureOptionChange,
                        disabled: !isAdmin
                    },
                    {
                        type: ComponentType.Switch,
                        label: t('configuration.prevent-leave-session-redirect'),
                        key: 'preventLeaveSessionRedirect',
                        statePath: 'featureSettings.preventLeaveSessionRedirect',
                        onChange: onFeatureSettingsChange,
                        disabled: !isAdmin
                    }
                ]
            }
        ],
        [
            t,
            isAdmin,
            isTeams,
            dialogOptions,
            downloadsDisabled,
            initialPermissionPublic,
            adminSettings.featureSettings.options.recordingsDisablePublicOption,
            onSwitchChange,
            onFeatureSettingsChange,
            onFeatureOptionChange,
            onRecordingSettingsChange
        ]
    );

    const flatSearchItems = useMemo(() => {
        const allSectionGroups = [
            { tab: ConfigTabs.BeforeSession, sections: beforeSessionConfigSections },
            { tab: ConfigTabs.InSession, sections: inSessionConfigSections },
            { tab: ConfigTabs.AfterSession, sections: afterSessionConfigSections }
        ];

        return allSectionGroups.flatMap(({ tab, sections }) =>
            sections.flatMap((section) =>
                section.fields.flatMap((field) => {
                    const parent = {
                        label: typeof field.label === 'string' ? field.label : '',
                        key: field.key,
                        sectionTitle: section.title,
                        tab,
                        fieldRef: field
                    };

                    const children =
                        field.children?.map((child) => ({
                            label: typeof child.label === 'string' ? child.label : '',
                            key: child.key,
                            sectionTitle: section.title,
                            tab,
                            fieldRef: child
                        })) || [];

                    return [parent, ...children];
                })
            )
        );
    }, [beforeSessionConfigSections, inSessionConfigSections, afterSessionConfigSections]);

    const fuzzySettingsSearch = useMemo(
        () =>
            new Fuse(flatSearchItems, {
                keys: ['label'],
                threshold: 0.3,
                includeScore: true
            }),
        [flatSearchItems]
    );

    const filteredSections = useMemo(() => {
        if (!searchTerm) return [];

        const results = fuzzySettingsSearch.search(searchTerm);
        return results.map((result) => result.item);
    }, [searchTerm, fuzzySettingsSearch]);

    const matchedKeys = useMemo(() => new Set(filteredSections.map((s) => s.key).filter(Boolean)), [filteredSections]);

    const highlightMatch = (text: string, query: string) => {
        const index = text.toLowerCase().indexOf(query.toLowerCase());
        if (index === -1) return text;

        return (
            <>
                {text.slice(0, index)}
                <mark>{text.slice(index, index + query.length)}</mark>
                {text.slice(index + query.length)}
            </>
        );
    };

    const handleResultClick = (result: FlatSearchItem) => {
        setActiveTab(result.tab);

        // Wait for tab content to render
        window.setTimeout(() => {
            const el = document.getElementById(result.key);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 50);
    };

    return (
        <SettingsContainer>
            <HeaderRow>
                <HeadingText variant="h1">{t('configuration.title')}</HeadingText>
                <StyledAutocomplete
                    freeSolo
                    options={filteredSections}
                    getOptionLabel={(option: string | FlatSearchItem) =>
                        typeof option === 'string' ? option : option.label || ''
                    }
                    inputValue={searchTerm}
                    onInputChange={(e, val) => setSearchTerm(val)}
                    renderOption={(props, option) => {
                        if (typeof option === 'string') {
                            return <li {...props}>{option}</li>;
                        }
                        return (
                            <StyledMenuItem
                                {...props}
                                key={option.key}
                                onClick={() => handleResultClick(option)}
                                sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                }}
                            >
                                <small>
                                    {`${option.tab} • ${option.sectionTitle} • `}
                                    {highlightMatch(option.label, searchTerm)}
                                </small>
                            </StyledMenuItem>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={t('configuration.search-settings')}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    )}
                />
            </HeaderRow>
            <TopLevelTabs activeTab={activeTab} onChange={setActiveTab} />
            {activeTab === ConfigTabs.BeforeSession && (
                <>
                    {beforeSessionConfigSections.map((section, idx) => {
                        const hasMatch = section.fields?.some(
                            (field) =>
                                matchedKeys.has(field.key) ||
                                field.children?.some((child) => matchedKeys.has(child.key))
                        );
                        return (
                            <ConfigSection
                                key={idx}
                                values={values}
                                fieldErrors={fieldErrors}
                                state={adminSettings}
                                {...section}
                                {...(Boolean(searchTerm) && hasMatch ? { expanded: true } : {})}
                                matchedKeys={matchedKeys}
                            />
                        );
                    })}
                </>
            )}
            {activeTab === ConfigTabs.InSession && (
                <>
                    {inSessionConfigSections.map((section, idx) => {
                        const hasMatch = section.fields?.some(
                            (field) =>
                                matchedKeys.has(field.key) ||
                                field.children?.some((child) => matchedKeys.has(child.key))
                        );
                        return (
                            <ConfigSection
                                key={idx}
                                values={values}
                                fieldErrors={fieldErrors}
                                state={adminSettings}
                                {...section}
                                {...(Boolean(searchTerm) && hasMatch ? { expanded: true } : {})}
                                matchedKeys={matchedKeys}
                            />
                        );
                    })}
                </>
            )}
            {activeTab === ConfigTabs.AfterSession && (
                <>
                    {afterSessionConfigSections.map((section, idx) => {
                        const hasMatch = section.fields?.some(
                            (field) =>
                                matchedKeys.has(field.key) ||
                                field.children?.some((child) => matchedKeys.has(child.key))
                        );
                        return (
                            <ConfigSection
                                key={idx}
                                values={values}
                                fieldErrors={fieldErrors}
                                state={adminSettings}
                                {...section}
                                {...(Boolean(searchTerm) && hasMatch ? { expanded: true } : {})}
                                matchedKeys={matchedKeys}
                            />
                        );
                    })}
                </>
            )}
            <SaveButtonContainer>
                <SaveButton isSaving={isSavingSettingsData} disabled={isSavingDisabled} onClick={handleSaveSettings} />
            </SaveButtonContainer>
        </SettingsContainer>
    );
};
export default ConfigurationPage;
// This code defines a ConfigurationPage component that manages different configuration tabs for an admin settings page.
